// @/components/auth/AuthLayout.jsx
// Layout for auth screens — logo dropdown, language selector, LayoutDefault wrapper

import React from "react";
import clsx from "clsx";
import PlatformLogo from "@/assets/logos/PlatformLogo";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/reusable/dropdown';
import { SidebarItem } from '@/components/reusable/sidebar';
import { Listbox, ListboxLabel, ListboxOption } from '@/components/reusable/listbox';
import PrivacyIcon from "@/assets/icons/PrivacyIcon";
import TermsIcon from "@/assets/icons/TermsIcon";
import ChevronIcon from "@/assets/icons/ChevronIcon";
import { useContentSidebar } from '@/hooks/content/useContentSidebar';
import { useLanguage } from '@/components/providers/LanguageContext';
import { getSupportedLocales } from '@/components/providers/LanguageSelector';
import CountryFlag from '@/components/reusable/CountryFlag';
import LayoutDefault from "@/components/layout/default";

import { BRAND_NAME } from "@/config/brand";
const AuthLayout = ({ children, className }) => {
  const sidebarContent = useContentSidebar();
  const { locale, setLocale } = useLanguage();

  const languages = getSupportedLocales().map(lang => ({
    value: lang.code,
    label: lang.name,
    flag: lang.flag
  }));

  const handleLanguageChange = (newLocale) => {
    if (newLocale !== locale) {
      setLocale(newLocale);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forceContentRefresh', {
          detail: { locale: newLocale }
        }));
      }, 100);
    }
  };

  return (
    <LayoutDefault className={clsx(className)}>
      <header className="absolute top-0 left-0 w-full flex justify-between px-12 pt-12">
        <div className="w-64">
          <Dropdown>
            <DropdownButton as={SidebarItem}>
              <div className="flex items-center justify-between w-full cursor-pointer">
                <PlatformLogo />
                <ChevronIcon className="size-5 text-zinc-600" />
              </div>
            </DropdownButton>
            <DropdownMenu className="min-w-64" anchor="bottom start">
              <DropdownItem as="a" href="https://trust.ctrl-s.io/" target="_blank" rel="noopener noreferrer">
                <PrivacyIcon />
                <DropdownLabel>{sidebarContent?.privacyPolicyLabel || 'Trust Center'}</DropdownLabel>
              </DropdownItem>
              <DropdownItem as="a" href="https://ctrl-s.io/" target="_blank" rel="noopener noreferrer">
                <TermsIcon />
                <DropdownLabel>{sidebarContent?.aboutLabel || `About ${BRAND_NAME}`}</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
        <div className="mt-1">
          <Listbox
            value={locale}
            onChange={handleLanguageChange}
            aria-label="Select language"
            className="w-40!"
          >
            {languages.map(lang => (
              <ListboxOption key={lang.value} value={lang.value}>
                <CountryFlag code={lang.flag} size="xs" />
                <ListboxLabel>{lang.label}</ListboxLabel>
              </ListboxOption>
            ))}
          </Listbox>
        </div>
      </header>
      {children}
    </LayoutDefault>
  );
};

export default AuthLayout;
